import React, { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot } from '@fortawesome/pro-regular-svg-icons';
import { Job } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import Button from '../Button';

const CANCEL_JOB_REASON_OPTIONS = [
  'No longer needed',
  'Filled internally',
  'Budget cut',
  'Schedule Change',
  'Other',
] as const;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onSuccess?: () => void;
};

const CancelJobModal = ({ isOpen, onClose, job, onSuccess }: Props) => {
  const { cancelJob } = useJobStore();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherReason, setOtherReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOtherSelected = selectedReason === 'Other';
  const reasonToSend = isOtherSelected
    ? otherReason.trim() || 'Other'
    : selectedReason;

  const canConfirm =
    !!selectedReason && (!isOtherSelected || otherReason.trim().length > 0);

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason(null);
      setOtherReason('');
      setError(null);
      onClose();
    }
  };

  const handleConfirmCancel = async () => {
    if (!job?.id || !canConfirm || !reasonToSend) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const cancelledSuccessfully = await cancelJob(job.id, reasonToSend);
      if (cancelledSuccessfully) {
        onSuccess?.();
        handleClose();
      } else {
        setError('Failed to cancel job');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cancelling job');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-job-dialog-title"
      >
        <div className="px-6 pt-10 pb-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <ExclamationTriangleIcon
                className="h-9 w-9 text-red-600"
                aria-hidden={true}
              />
            </div>
            <h2
              id="cancel-job-dialog-title"
              className="mt-5 text-lg font-semibold text-gray-900"
            >
              Cancel this job?
            </h2>
            <p className="mt-2 max-w-sm text-sm text-gray-500">
              Are you sure you want to cancel this job? This action cannot be
              undone.
            </p>
          </div>

          <div className="mt-8">
            <p
              id="cancel-job-reason-label"
              className="mb-3 text-left text-sm font-medium text-gray-700"
            >
              Select a reason to continue
            </p>
            <div
              className="space-y-2"
              role="radiogroup"
              aria-labelledby="cancel-job-reason-label"
            >
              {CANCEL_JOB_REASON_OPTIONS.map((option) => (
                <div
                  key={option}
                  role="radio"
                  aria-checked={selectedReason === option}
                  tabIndex={0}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border p-4 text-left hover:bg-gray-50 ${
                    selectedReason === option
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}
                  onClick={() => setSelectedReason(option)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedReason(option);
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={selectedReason === option ? faCircleDot : faCircle}
                    className={`mr-1 ${
                      selectedReason === option
                        ? 'text-blue-500'
                        : 'text-gray-300'
                    }`}
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {option}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {isOtherSelected && (
            <div className="mt-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Please describe your reason
              </label>
              <textarea
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Describe your reason..."
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />
            </div>
          )}
          {error && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-center gap-3 border-t border-gray-200 px-6 py-6">
          <Button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="!min-w-[7rem] !rounded-lg !border !border-gray-300 !bg-white !px-5 !py-2.5 !text-sm !font-medium !text-gray-700 hover:!bg-gray-50 disabled:opacity-50"
          >
            Keep Job
          </Button>
          <Button
            type="button"
            onClick={handleConfirmCancel}
            disabled={!canConfirm || isSubmitting}
            className="!min-w-[7rem] !rounded-lg !bg-red-600 !px-5 !py-2.5 !text-sm !font-medium !text-white hover:!bg-red-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CancelJobModal;
